import { Link } from "react-router-dom";
import { ArrowUpRight, Hammer, Trees } from "lucide-react";
import { CONCRETE_SERVICES, LANDSCAPE_SERVICES, IMAGES } from "../data/site";

function Pillar({ kind, title, items, image, tagline }) {
  const Icon = kind === "concrete" ? Hammer : Trees;
  const accent = kind === "concrete" ? "text-stone-900" : "text-emerald-700";
  return (
    <div className="bg-white border border-stone-200 p-8 md:p-10 group" data-testid={`pillar-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon size={22} className={accent} strokeWidth={2.2} />
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-stone-500">
            {kind === "concrete" ? "Concrete Services" : "Landscape Services"}
          </span>
        </div>
        <Link to="/services" aria-label="View all services" className="text-stone-400 hover:text-red-600">
          <ArrowUpRight size={20} />
        </Link>
      </div>

      <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight text-stone-900 mt-5 leading-tight">
        {title}
      </h3>
      <p className="mt-3 text-stone-600 leading-relaxed">{tagline}</p>

      <div className="mt-6 aspect-[4/3] overflow-hidden border border-stone-200">
        <img
          src={image}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
        />
      </div>

      <ul className="mt-7 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
        {items.slice(0, 10).map((s) => (
          <li key={s.name} className="flex items-center gap-2 text-stone-700">
            <span className={`w-1.5 h-1.5 ${s.featured ? "bg-red-600" : "bg-stone-400"}`} />
            <span className={s.featured ? "font-semibold text-red-700" : ""}>
              {s.name}
              {s.featured ? " ★" : ""}
            </span>
          </li>
        ))}
      </ul>

      <Link
        to={kind === "concrete" ? "/services" : "/xeriscape"}
        className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-stone-900 hover:text-red-600 border-b-2 border-stone-900 hover:border-red-600 pb-1"
        data-testid={`pillar-${kind}-cta`}
      >
        {kind === "concrete" ? "See all concrete work" : "Explore landscaping & Xeriscape"} →
      </Link>
    </div>
  );
}

export default function ServicesSplit() {
  return (
    <section
      data-testid="services-split"
      className="bg-stone-50 py-20 md:py-28 border-t border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-600">
            What we build
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tighter text-stone-900 leading-[1.02]">
            Two specialties.
            <br /> One Austin contractor.
          </h2>
          <p className="mt-4 text-stone-600 text-lg max-w-2xl">
            From the foundation under your house to the xeriscape at your curb, Texan Strong
            Builds self-performs every concrete and landscape trade in-house across Austin, TX.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Pillar
            kind="concrete"
            title="Concrete that holds Texas."
            tagline="Texan Strong Builds pours reinforced, code-compliant concrete finished for Austin's clay soil and brutal sun — foundations, driveways, stamped patios, retaining walls and full commercial flatwork."
            image={IMAGES.concreteWorker}
            items={CONCRETE_SERVICES}
          />
          <Pillar
            kind="landscape"
            title="Landscape built for the drought."
            tagline="Texan Strong Builds designs xeriscape, native planting, drip irrigation and decomposed-granite hardscape — beautiful Austin yards that survive Stage 3 water restrictions."
            image={IMAGES.xeriscape1}
            items={LANDSCAPE_SERVICES}
          />
        </div>
      </div>
    </section>
  );
}
